import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import DrawerComponent from '../../components/DrawerComponent';
import LoaderSmall from '../../components/LoaderSmall';
import PaginationComponent from '../../components/PaginationComponent';
import { useApi } from '../../components/ApiProvider';
import authQueries from '../../queries/authQueries';
import dropdownQueries from '../../queries/dropdownQueries';
import { Search, User, Mail, Phone, ArrowRight, Filter } from 'lucide-react';
import { Button, Popover, PopoverContent, PopoverTrigger } from '@nextui-org/react';
import { Clock, Check, Crown, XCircle } from 'lucide-react';
import moment from 'moment/moment';
import useDebounce from '../../hooks/useDebounce';
import { toggleSnackbar } from '../../redux/reducers/optionsReducers';

const UsersList = () => {

    const dispatch = useDispatch()

    const [result, setResult] = useState([]);
    const [roles, setRoles] = useState([]);
    const [search, setSearch] = useState("");
    const [subscription, setSubscription] = useState("");
    const [loading, setLoading] = useState(true)

    const user = useSelector((state) => state.auth.user)
    const currentPage = useSelector((state) => state.pagination.currentPage);

    const inputFields = [
        {
            name: 'fname',
            type: 'text',
            label: 'First Name',
            required: true
        },
        {
            name: 'lname',
            type: 'text',
            label: 'Last Name',
            required: true
        },
        {
            name: 'email',
            type: 'email',
            label: 'Email',
            required: true
        },
        {
            name: 'phone',
            type: 'text',
            label: 'Mobile Number',
            required: false
        },
        {
            name: 'role_id',
            type: 'select',
            label: 'Role',
            options: roles,
            required: user?.role?.role  !== "MANAGER",
            hide: user?.role?.role === "MANAGER"
        }
    ];

    const { apiData, setApiDataAndNotify } = useApi();

    const userAddorEdit = dropdownQueries.useUserAddOrEditMutation(
        (res) => {
            setRoles(res?.roles);
        }
    );

    useEffect(() => {
        userAddorEdit.mutateAsync({});
    }, []);

    const userList = authQueries.useUserListMutation(
        (res) => {
          setLoading(false)
          setResult(res);
        }
    );

    const debouncedSearch = useDebounce(search, 400);

    useEffect(() => {
        userList.mutateAsync({ page: currentPage, size: 25, search: search, subscription: subscription });
    }, [apiData, currentPage, debouncedSearch, subscription]);

    const createUser = authQueries.useCreateUserMutation(
        (res) => {
          if(res?.success){  
            setApiDataAndNotify(res);
            dispatch(toggleSnackbar({
                open: true,
                severity: "success",
                position: "top-right",
                message: "New user added!",
                description: "You’ve successfully created a new user account."
              }))
          }
        }
    );

    const updateUser = authQueries.useUpdateUserMutation(
        (res) => {
            if(res?.success){
              setApiDataAndNotify(res);
              dispatch(toggleSnackbar({
                open: true,
                severity: "success",
                position: "top-right",
                message: "Update successful",
                description: "User details were updated."
              }))
            }
        }
    );

    const handleSubscription = (item, status) => {
      updateSubscription.mutateAsync({id: item?.id, subscription: status || "Trial"});
    }

    const updateSubscription = authQueries.useSubscriptionStatusChange(
      (res)=>{
        if(res?.success){
          setApiDataAndNotify(res);
          dispatch(toggleSnackbar({
            open: true,
            severity: "success",
            position: "top-right",
            message: "Update successful",
            description: "Subscription status has been updated."
          }))
        }
      }
    )


  return (
    <div className="rounded-xl overflow-hidden backdrop-blur-sm">
      <div className="bg-white bg-opacity-90 rounded-xl overflow-hidden shadow-[0_8px_32px_0_rgba(31,38,135,0.15)] border border-gray-200/50 backdrop-filter backdrop-blur-xl">
        <div className="px-8 pt-6 pb-4 border-b border-gray-100 relative">
          
          <div className="flex flex-col md:flex-row justify-end items-center gap-4">
            <div className="relative w-full md:w-80">
                <select
                type="text"
                placeholder="Search by name, mobile, email"
                className="w-full h-12 pl-12 pr-6 rounded-2xl border-2 border-gray-200/60 bg-white/90 backdrop-blur-sm focus:outline-none focus:border-indigo-400 focus:bg-white shadow-lg transition-all duration-300 text-gray-800 placeholder:text-gray-500"
                value={subscription}
                onChange={(e) => setSubscription(e.target.value)}
                >
                  <option value="">Select Status</option>
                  <option value="Premium">Premium Users</option>
                  <option value="Trial">Trial Users</option>
                  <option value="Expired">Expired Users</option>
                </select>
                <div className="absolute left-2 top-1/2 transform -translate-y-1/2">
                  <div className="w-8 h-8  bg-primaryGradient rounded-xl flex items-center justify-center">
                    <Filter className="w-4 h-4 text-white" />
                  </div>
                </div>
                {search && (
                  <button
                  onClick={() => setSearch('')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center transition-all duration-200"
                  >
                    <span className="text-gray-500 text-xs">✕</span>
                  </button>
                )}
            </div>
            <div className="relative w-full md:w-64">
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 rounded-lg blur"></div>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search users..."
                  className="w-full h-11 pl-11 pr-4 rounded-lg border border-gray-200/60 bg-white/80 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent shadow-sm transition-all"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <div className="absolute left-3 top-2.5 h-6 w-6 text-indigo-500 opacity-70">
                  <Search size={20} />
                </div>
              </div>
            </div>
            <DrawerComponent
            entityForm={createUser}
            inputFields={inputFields}
            btnName={'Create User'}
            header={'Create User'}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="w-full flex items-center justify-center py-20">
              <LoaderSmall />
            </div>
          ) : (
            <Fragment>
              {result?.data?.totalItems > 0 && (
                <div className="px-8 py-3 flex justify-between items-center bg-gray-50/80 backdrop-blur-sm">
                  <div className="text-sm text-gray-500">
                    Showing {result?.data?.currentPage * 25 + 1} to {result?.data?.currentPage * 25 + result?.data?.items?.length} of {result?.data?.totalItems} entries
                  </div>
                </div>
              )}
              
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-gray-50 to-gray-100/80 backdrop-blur-sm">
                    <th className="pl-8 pr-3 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider text-left">#</th>
                    <th className="px-3 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider text-left">User</th>
                    <th className="px-3 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider text-left">Contact</th>
                    <th className="px-3 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider text-left">Role</th>
                    <th className="px-3 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider text-left w-[400px]">Vessel</th>
                    { user?.role?.role === "SADMIN" &&
                      <th className="px-3 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider text-left">Status</th>
                    }
                    { user?.role?.role === "SADMIN" &&
                      <th className="px-3 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider text-left w-[400px]">Last subscription date</th>
                    }
                    <th className="px-8 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {result?.data?.items?.map((item, index) => (
                    <>
                      <tr 
                      key={user?.id} 
                      className="hover:bg-gradient-to-r hover:from-indigo-50/50 hover:to-transparent transition-colors duration-300"
                      >
                        <td className="pl-8 pr-3 py-3 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className="text-sm text-gray-500 font-medium">
                              {(index + 1) + (result?.data?.currentPage * 25)}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="flex-shrink-0 w-9 h-9 bg-primaryGradient rounded-lg flex items-center justify-center text-white font-medium text-sm shadow-md">
                              {item?.fname?.charAt(0)}{user?.lname?.charAt(0)}
                            </div>
                            <div>
                              <div className="text-sm text-gray-500">{item?.fname} {item?.lname}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <div className="flex flex-col gap-1">
                            {item?.email && (
                              <div className="flex items-center text-sm text-gray-600">
                                <Mail size={14} className="mr-1.5 text-gray-400" /> 
                                <span className="truncate max-w-[180px]">{item?.email}</span>
                              </div>
                            )}
                            {user?.phone && (
                              <div className="flex items-center text-sm text-gray-600">
                                <Phone size={14} className="mr-1.5 text-gray-400" /> 
                                <span>{item?.phone}</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <div className="px-2.5 py-1 rounded-md bg-indigo-100/70 backdrop-blur-sm border border-indigo-200/50 text-xs font-medium text-indigo-800 w-fit">
                            {item?.role?.name}
                          </div>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap space-y-2">
                          <div className="flex flex-wrap gap-2 relative group">
                            {item?.vessels?.slice(0, 2).map((vessel, index) => (
                              <div key={index} className="px-2.5 py-1 rounded-md bg-blue-100/70 backdrop-blur-sm border border-blue-200/50 text-xs font-medium text-blue-800 w-fit">
                                {vessel?.name}
                              </div>
                            ))}

                            {item?.vessels?.length > 2 && (
                              <div className="relative group">
                                <div className="px-2.5 py-1 rounded-md bg-blue-100/70 backdrop-blur-sm border border-blue-200/50 text-xs font-medium text-blue-800 w-fit">
                                  +{item?.vessels?.length - 2} more
                                </div>

                                <div className="absolute z-50 hidden group-hover:flex top-full mt-1 left-1/2 -translate-x-1/2 w-max max-w-[250px] bg-gray-800 text-white text-xs rounded-md px-3 py-2 shadow-lg whitespace-pre-wrap">
                                  {item?.vessels?.slice(2).map(v => v.name).join(', ')}
                                </div>
                              </div>
                            )}

                            {item?.vessels?.length === 0 && (
                              <div key={index} className="px-2.5 py-1 rounded-md bg-pink-100/70 backdrop-blur-sm border border-pink-200/50 text-xs font-medium text-pink-600 w-fit">
                                No Vessel Assigned
                              </div>
                            )}
                          </div>
                        </td>
                        {
                          user?.role?.role === "SADMIN" &&
                          <td className="px-3 py-3 whitespace-nowrap">
                            <PremiumSubscriptionUI user={item} handleSubscription={handleSubscription} />
                          </td>
                        }
                        {
                          user?.role?.role === "SADMIN" &&
                          <td className="px-3 py-3 whitespace-nowrap">
                            {moment(item?.last_subscription_date).format("DD-MM-YYYY")}
                          </td>
                        }
                        <td className="px-8 py-3 whitespace-nowrap text-right">
                          <div className='gap-2 flex items-center justify-end'>
                            <DrawerComponent 
                              data={item} 
                              edit={true} 
                              entityForm={updateUser} 
                              inputFields={inputFields} 
                              btnName={'Update User'} 
                              header={'Update User'} 
                              className="inline-flex items-center gap-1 bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-1.5 px-3 rounded-md text-xs font-medium shadow-sm hover:shadow-md transition-all duration-300"
                            />
                          </div>
                        </td>
                      </tr>
                    </>
                  ))}
                  
                  {result?.data?.items?.length === 0 && (
                    <tr>
                      <td colSpan="8" className="px-4 py-12">
                        <div className="flex flex-col items-center justify-center text-center">
                          <div className="w-16 h-16 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 rounded-full flex items-center justify-center mb-4">
                            <User size={32} className="text-indigo-500" />
                          </div>
                          <h3 className="text-gray-700 font-medium mb-2">No users found</h3>
                          <p className="text-gray-500 text-sm max-w-md mb-4">
                            We couldn't find any users matching your search criteria
                          </p>
                          <button 
                            onClick={() => setSearch('')}
                            className="flex items-center gap-1 text-indigo-600 text-sm font-medium"
                          >
                            Clear search
                            <ArrowRight size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                  
                </tbody>
              </table>
              
              <div className="border-t border-gray-100 p-4 bg-gradient-to-r from-gray-50/80 to-white/60 backdrop-blur-sm">
                <PaginationComponent result={result} />
              </div>
            </Fragment>
          )}
        </div>
      </div>
    </div>
  );
};

export default UsersList;

const PremiumSubscriptionUI = ({ user, handleSubscription }) => {
  // Status styles
  const getButtonStyle = (status) => {
    switch(status) {
      case 'Trial': return {
        bg: "bg-gradient-to-r from-amber-400 to-orange-400",
        hoverBg: "hover:bg-gradient-to-r hover:from-amber-500 hover:to-orange-500",
        shadow: "shadow-amber-200/30"
      };
      case 'Premium': return {
        bg: "bg-gradient-to-r from-indigo-500 to-violet-600",
        hoverBg: "hover:bg-gradient-to-r hover:from-indigo-600 hover:to-violet-700",
        shadow: "shadow-indigo-200/30"
      };
      case 'Expired': return {
        bg: "bg-gradient-to-r from-rose-400 to-pink-500",
        hoverBg: "hover:bg-gradient-to-r hover:from-rose-500 hover:to-pink-600",
        shadow: "shadow-rose-200/30"
      };
      default: return {
        bg: "bg-gradient-to-r from-amber-400 to-orange-400",
        hoverBg: "hover:bg-gradient-to-r hover:from-amber-500 hover:to-orange-500",
        shadow: "shadow-amber-200/30"
      };
    };
  };
  
  // Status icons
  const getButtonIcon = (status) => {
    switch(status) {
      case 'Trial': return <Clock size={14} className="text-white" />;
      case 'Premium': return <Crown size={14} className="text-white" />;
      case 'Expired': return <XCircle size={14} className="text-white" />;
      default: return <Clock size={14} className="text-white" />;
    }
  };
  
  return (
    <Popover placement="left">
      <PopoverTrigger>
        <Button
          size="sm" 
          className={`${getButtonStyle(user?.subscription).bg} text-white shadow-md hover:shadow-lg transition-all duration-300 h-7`}
        >
          {getButtonIcon(user?.subscription)} {user?.subscription}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 border-0 shadow-xl rounded-xl overflow-hidden">
        <div className="w-48">
          {/* Header */}
          <div className="p-3 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-800">Change Plan</h3>
          </div>
          
          {/* Vertical Buttons */}
          <div className="flex flex-col p-2 gap-2">
            {/* Trial Button */}
            <button 
              onClick={() => handleSubscription(user, 'Trial')}
              className={`relative group overflow-hidden rounded-lg ${getButtonStyle('Trial').bg} ${getButtonStyle('Trial').hoverBg} text-white p-0.5 shadow-md ${getButtonStyle('Trial').shadow} hover:shadow-lg transition-all duration-300`}
            >
              {/* Decorative glow effect */}
              <div className="absolute top-0 left-0 w-12 h-12 rounded-full bg-white/10 blur-xl transform -translate-x-6 -translate-y-6"></div>
              
              <div className="relative z-10 flex items-center justify-between px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                    <Clock size={12} />
                  </div>
                  <span className="text-sm font-medium">Trial</span>
                </div>
                {user?.subscription === 'Trial' && (
                  <div className="w-4 h-4 rounded-full bg-white/20 flex items-center justify-center">
                    <Check size={10} />
                  </div>
                )}
              </div>
            </button>
            
            {/* Premium Button */}
            <button 
              onClick={() => handleSubscription(user, 'Premium')}
              className={`relative group overflow-hidden rounded-lg ${getButtonStyle('Premium').bg} ${getButtonStyle('Premium').hoverBg} text-white p-0.5 shadow-md ${getButtonStyle('Premium').shadow} hover:shadow-lg transition-all duration-300`}
            >
              {/* Decorative glow effect */}
              <div className="absolute top-0 left-0 w-12 h-12 rounded-full bg-white/10 blur-xl transform -translate-x-6 -translate-y-6"></div>
              
              <div className="relative z-10 flex items-center justify-between px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                    <Crown size={12} />
                  </div>
                  <span className="text-sm font-medium">Premium</span>
                </div>
                {user?.subscription === 'Premium' && (
                  <div className="w-4 h-4 rounded-full bg-white/20 flex items-center justify-center">
                    <Check size={10} />
                  </div>
                )}
              </div>
            </button>
            
            {/* Expired Button */}
            <button 
              onClick={() => handleSubscription(user, 'Expired')}
              className={`relative group overflow-hidden rounded-lg ${getButtonStyle('Expired').bg} ${getButtonStyle('Expired').hoverBg} text-white p-0.5 shadow-md ${getButtonStyle('Expired').shadow} hover:shadow-lg transition-all duration-300`}
            >
              {/* Decorative glow effect */}
              <div className="absolute top-0 left-0 w-12 h-12 rounded-full bg-white/10 blur-xl transform -translate-x-6 -translate-y-6"></div>
              
              <div className="relative z-10 flex items-center justify-between px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                    <XCircle size={12} />
                  </div>
                  <span className="text-sm font-medium">Expired</span>
                </div>
                {user?.subscription === 'Expired' && (
                  <div className="w-4 h-4 rounded-full bg-white/20 flex items-center justify-center">
                    <Check size={10} />
                  </div>
                )}
              </div>
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};